/**
 * Filter Summary Component
 * Displays a visual summary of currently applied filters
 */

import {
  Box,
  Paper,
  Typography,
  Chip,
  Divider,
  IconButton,
  Collapse,
  Fade,
} from '@mui/material'
import { useState } from 'react'
import {
  ExpandMore,
  ExpandLess,
  Person,
  Category,
  School,
  Group,
  Assignment,
  FilterList,
} from '@mui/icons-material'
import dayjs from 'dayjs'
import { useFilters } from '../../hooks/use-filters'
import { useDashboardData } from '../../hooks/use-dashboard-data'
import { FILTER_COLORS } from './filter-colors'

interface FilterSummaryProps {
  filterState: ReturnType<typeof useFilters>
}

export default function FilterSummary({ filterState }: FilterSummaryProps) {
  let { filters } = filterState
  let [expanded, setExpanded] = useState(false)

  // Get data for displaying names instead of IDs
  let { linkedServices, groups, lightweightUsers } = useDashboardData(
    filters.linkedServiceID,
  )

  // Helper to get linked service name
  let linkedServiceName = linkedServices.find(
    (service) => service.id === filters.linkedServiceID,
  )?.name

  // Helper to get user names
  let selectedUserNames = lightweightUsers
    .filter((user) => filters.selectedUsers.includes(user.id))
    .map((user) => `${user.firstName} ${user.lastName}`)

  // Helper to get group names
  let selectedGroupNames = groups
    .filter((group) => filters.selectedGroups.includes(group.id || ''))
    .map((group) => group.name)

  // Activity type labels
  let activityTypeLabels = filters.selectedActivityTypes.map((type) => {
    switch (type) {
      case 'ce-activity':
        return 'CE Activity'
      case 'assignment':
        return 'Assignment'
      default:
        return type
    }
  })

  // Format date range
  let dateRangeText = (() => {
    if (filters.timeRange === 'custom') {
      let start = filters.customStartDate
        ? dayjs(filters.customStartDate).format('MMM D, YYYY')
        : 'Start'
      let end = filters.customEndDate
        ? dayjs(filters.customEndDate).format('MMM D, YYYY')
        : 'End'
      return `${start} - ${end}`
    }

    switch (filters.timeRange) {
      case 'week':
        return 'Last Week'
      case 'month':
        return 'Last Month'
      case 'quarter':
        return 'Last Quarter'
      case 'year':
        return 'Last Year'
      default:
        return 'Unknown Range'
    }
  })()

  // Count active filters
  let activeFilterCount =
    (filters.selectedUsers.length > 0 ? 1 : 0) +
    (filters.selectedCategories.length > 0 ? 1 : 0) +
    (filters.selectedLicenseTypes.length > 0 ? 1 : 0) +
    (filters.selectedGroups.length > 0 ? 1 : 0) +
    (filters.selectedActivityTypes.length > 0 ? 1 : 0)

  // If no filters are applied, show minimal summary
  if (activeFilterCount === 0) {
    return (
      <Fade in>
        <Paper
          sx={{
            mb: 3,
            p: 2,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FilterList sx={{ color: 'text.secondary' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Showing all activities for{' '}
                <strong>{linkedServiceName || 'Selected Service'}</strong> •{' '}
                {dateRangeText}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Fade>
    )
  }

  return (
    <Fade in>
      <Paper
        sx={{
          mb: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'primary.main',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Header with basic info */}
        <Box
          sx={{
            p: 2,
            bgcolor: 'primary.50',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FilterList sx={{ color: 'primary.main' }} />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {activeFilterCount} Filter{activeFilterCount !== 1 ? 's' : ''}{' '}
                Applied
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {linkedServiceName} • {dateRangeText}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            size="small"
            sx={{
              color: 'primary.main',
              '&:hover': { bgcolor: 'primary.100' },
            }}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        {/* Expanded filter details */}
        <Collapse in={expanded}>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Box className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {/* Users Filter */}
              {filters.selectedUsers.length > 0 && (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      Users ({filters.selectedUsers.length})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {selectedUserNames.slice(0, 3).map((name) => (
                      <Chip
                        key={name}
                        label={name}
                        size="small"
                        variant="outlined"
                        color={FILTER_COLORS.users}
                      />
                    ))}
                    {selectedUserNames.length > 3 && (
                      <Chip
                        label={`+${selectedUserNames.length - 3} more`}
                        size="small"
                        variant="outlined"
                        color={FILTER_COLORS.users}
                      />
                    )}
                  </Box>
                </Box>
              )}

              {/* Categories Filter */}
              {filters.selectedCategories.length > 0 && (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Category sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      Categories ({filters.selectedCategories.length})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {filters.selectedCategories.slice(0, 2).map((category) => (
                      <Chip
                        key={category}
                        label={category}
                        size="small"
                        variant="outlined"
                        color={FILTER_COLORS.categories}
                      />
                    ))}
                    {filters.selectedCategories.length > 2 && (
                      <Chip
                        label={`+${filters.selectedCategories.length - 2} more`}
                        size="small"
                        variant="outlined"
                        color={FILTER_COLORS.categories}
                      />
                    )}
                  </Box>
                </Box>
              )}

              {/* License Types Filter */}
              {filters.selectedLicenseTypes.length > 0 && (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <School sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      License Types ({filters.selectedLicenseTypes.length})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {filters.selectedLicenseTypes.slice(0, 2).map((license) => (
                      <Chip
                        key={license}
                        label={license}
                        size="small"
                        variant="outlined"
                        color={FILTER_COLORS.licenseTypes}
                      />
                    ))}
                    {filters.selectedLicenseTypes.length > 2 && (
                      <Chip
                        label={`+${filters.selectedLicenseTypes.length - 2} more`}
                        size="small"
                        variant="outlined"
                        color={FILTER_COLORS.licenseTypes}
                      />
                    )}
                  </Box>
                </Box>
              )}

              {/* Groups Filter */}
              {filters.selectedGroups.length > 0 && (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Group sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      Groups ({filters.selectedGroups.length})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {selectedGroupNames.slice(0, 2).map((groupName) => (
                      <Chip
                        key={groupName}
                        label={groupName}
                        size="small"
                        variant="outlined"
                        color={FILTER_COLORS.groups}
                      />
                    ))}
                    {selectedGroupNames.length > 2 && (
                      <Chip
                        label={`+${selectedGroupNames.length - 2} more`}
                        size="small"
                        variant="outlined"
                        color={FILTER_COLORS.groups}
                      />
                    )}
                  </Box>
                </Box>
              )}

              {/* Activity Types Filter */}
              {filters.selectedActivityTypes.length > 0 && (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Assignment
                      sx={{ fontSize: 16, color: 'text.secondary' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Activity Types ({filters.selectedActivityTypes.length})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {activityTypeLabels.map((label) => (
                      <Chip
                        key={label}
                        label={label}
                        size="small"
                        variant="outlined"
                        color={FILTER_COLORS.activityTypes}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Collapse>
      </Paper>
    </Fade>
  )
}
